import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/api';

export type ApiKeyStatus = 'active' | 'revoked' | 'rotating';

export interface ApiKeyItem {
  id: string;
  key_prefix: string;
  label: string | null;
  status: ApiKeyStatus;
  created_at: string;
  last_used_at: string | null;
}

export interface CreateApiKeyResult {
  id: string;
  key: string;
  key_prefix: string;
  label: string | null;
}

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface ApiKeyError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): ApiKeyError {
  const e = new Error(stage) as ApiKeyError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

export const apiKeysQueryKey = ['credentials', 'api-keys'] as const;

export interface ApiKeysPage {
  items: ApiKeyItem[];
  next_cursor: string | null;
}

// 100 is MAX_LIMIT on the server (PaginationQuery::clamped_limit), so a normal
// account's keys load in a single request.
const PAGE_LIMIT = 100;

export function useApiKeys() {
  const api = useApi();
  const query = useInfiniteQuery({
    queryKey: apiKeysQueryKey,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }): Promise<ApiKeysPage> => {
      const { data, error, response } = await api.GET('/auth/api-keys', {
        params: { query: { limit: PAGE_LIMIT, ...(pageParam != null ? { cursor: pageParam } : {}) } },
      });
      if (error) throw toError('api_keys_list_failed', response, error);
      const payload = data as { items?: ApiKeyItem[]; next_cursor?: string | null } | undefined;
      return {
        items: payload?.items ?? [],
        next_cursor: payload?.next_cursor ?? null,
      };
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const items: ApiKeyItem[] = query.data?.pages.flatMap((p) => p.items) ?? [];
  return { ...query, items };
}

export function useCreateApiKey() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<CreateApiKeyResult, ApiKeyError, { label: string | null }>({
    mutationFn: async ({ label }) => {
      const { data, error, response } = await api.POST('/auth/api-keys', {
        body: { label },
      });
      if (error) throw toError('api_key_create_failed', response, error);
      return data as unknown as CreateApiKeyResult;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: apiKeysQueryKey });
    },
  });
}

export function useRotateApiKey() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<CreateApiKeyResult, ApiKeyError, { id: string }>({
    mutationFn: async ({ id }) => {
      const { data, error, response } = await api.POST('/auth/api-keys/{id}/rotate', {
        params: { path: { id } },
      });
      if (error) throw toError('api_key_rotate_failed', response, error);
      return data as unknown as CreateApiKeyResult;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: apiKeysQueryKey });
    },
  });
}

export function useRevokeApiKey() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<void, ApiKeyError, { id: string }>({
    mutationFn: async ({ id }) => {
      const { error, response } = await api.DELETE('/auth/api-keys/{id}', {
        params: { path: { id } },
      });
      if (error) throw toError('api_key_revoke_failed', response, error);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: apiKeysQueryKey });
    },
  });
}

export function useUpdateApiKeyLabel() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation<void, ApiKeyError, { id: string; label: string | null }>({
    mutationFn: async ({ id, label }) => {
      const { error, response } = await api.PATCH('/auth/api-keys/{id}', {
        params: { path: { id } },
        body: { label },
      });
      if (error) throw toError('api_key_update_failed', response, error);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: apiKeysQueryKey });
    },
  });
}
